/**
 * The PixiJS implementation of the Director's {@link PresentationAnimator} seam — the
 * **renderer-coupled implementation behind a stable, renderer-agnostic interface**. The Director
 * core (`presentationDirector.ts`) only ever awaits the `Promise`s these methods return, so
 * swapping renderers is a rewrite of this one file, with the queue untouched.
 *
 * A {@link NodeRegistry} (id → the token's Pixi `Container`) is the bridge. Pixi tokens
 * have no persistent per-token component (`pixiCombatBoard.ts`'s `setTokens` rebuilds
 * every token container from scratch on each call), so the registration lives at the
 * point Pixi actually builds/tears down a container: `setTokens` calls
 * `registerNode`/`unregisterNode` itself, threaded in as a constructor param to
 * {@link mountCombatBoard}. A beat whose node is absent (not yet registered / already
 * torn down) resolves immediately — the state still commits, only the flourish is
 * skipped.
 *
 * **Anchor-point math (§5's invariant, preserved on purpose):** token containers are
 * direct children of `tokensContainer` with NO intermediate transform (only the shared
 * `world` root pans/zooms), so a token's own local `.x`/`.y` is already
 * `tokensContainer`-relative — no further coordinate-space conversion is needed. Pixi's
 * own `Container.width`/`.height` bounds getters give the footprint size directly.
 *
 * **`app.ticker`-driven.** `usePresentationDirector` builds this animator SYNCHRONOUSLY on the
 * map's first render, before `mountCombatBoard`'s async `Application` exists (the same mount-timing
 * gap the `NodeRegistry` has), so `tween()`/`shakeAndPulse` take a
 * {@link TickerRef} — a live handle populated asynchronously once the board mounts
 * (`CombatStage.tsx`'s mount effect calls `setTicker(handle.app.ticker)` in the same
 * `.then(handle)` that sets `boardHandleRef`). Every animation here rides `ticker.add`
 * once that ref is populated, so `ticker.maxFPS` genuinely caps every visual mutation on
 * this surface — "everything under `maxFPS`" is literally true here, not
 * just for the shared halo/label/cursor/ping ticker in `pixiCombatBoard.ts`. A bare
 * `requestAnimationFrame` loop remains ONLY as the pre-mount fallback (`ticker.get()`
 * returns `null`) — in practice no beat ever fires there, since nothing is registered in
 * the `NodeRegistry` until the first `setTokens` call, which only happens after mount.
 *
 * **The destroyed-node guard MUST survive both loop paths.** A `setTokens`
 * reconcile (`pixiCombatBoard.ts`) tears down a genuinely REMOVED token's container, and that
 * can be the very container a tween/shake is running against, out from under
 * it — a destroyed Pixi `Container` nulls its internal `_position`, so writing `.x`/`.y`/
 * `.tint` on it throws. Both `tween()` and `shakeAndPulse` check `node.destroyed` at
 * entry (before ever scheduling a frame) AND on every single tick (whichever loop is
 * driving it), resolving/settling immediately instead of writing — never weaken this;
 * see each function's own comment for the mechanism.
 */
import type { Container, Ticker } from "pixi.js";
import type { AttackFx } from "@lancer-vtt/domain";
import { AREA_OUTCOME_MS, DEFAULT_ATTACK_FX, DETONATION_MS, OUTCOME_MS, REDUCED_TRAVEL_MS, travelMs } from "./attackFxTiming";
import type { AttackOutcomeItem, AttackTravelItem, FxUnit } from "./attackFxItems";
import type { AnimAnchor, FloatSpec, ImpactSpec, SfxCue, TravelSpec } from "./presentationDirector";
import { asSquareCell } from "../../routes/home/combatCells";
import { sfxEngine } from "../sfx/SfxEngine";

/** Per-tile step duration for a move walk (ms) — each tile is its own ease-out tween (a rhythmic march). */
export const MOVE_STEP_MS = 130;
/** Duration of the in-place blink (teleport / GM-place) and the spawn/destroy fades (ms).
 *  Exported so the Director can use it as a beat's nominal duration for the backpressure estimate. */
export const FLOURISH_MS = 160;
/**
 * How long a floating-combat-text beat **paces** the serial pump (ms) — the gap before the next
 * AoE outcome floats. The Pixi float text runs its own rise/fade tween (slightly longer, so it's
 * still fading as the next pops); this only governs the queue's one-at-a-time rhythm.
 */
export const FLOAT_PACING_MS = 750;
/**
 * The compressed pace of a follow-on float (ms): every float after the first over the same token in
 * one resolution (`FloatSpec.followOn`, `resolution-step-beats` Decision 2 B). The developer tunes
 * this by eye; it must stay above {@link SHAKE_PULSE_MS}.
 */
export const FOLLOW_ON_FLOAT_PACING_MS = 400;
/** How long a chain link's caption (`AGGRESSION — BERSERKER`, `chained-resolution-sequence` 2.1)
 *  **paces** the serial pump (ms): read before the link plays, not a beat to skip past. The board
 *  holds the plate a little past this and fades it. */
export const CAPTION_PACING_MS = 900;
/** How long a `projectile` beat **paces** the serial pump (ms) — the marker's travel time, after
 *  which the next beat (its attack/miss outcome) plays. */
export const PROJECTILE_PACING_MS = 220;
/** How long an `impact` beat **paces** the serial pump (ms) — the burst's life before the next
 *  beat (its attack outcome) floats. */
export const IMPACT_PACING_MS = 200;
/** Total wall-clock of the shake+pulse (ms) — kept shorter than {@link FLOAT_PACING_MS} so the
 *  beat's wall-clock (`max(shake, float)`) is still just the float pacing. */
export const SHAKE_PULSE_MS = 260;
/** Peak horizontal offset (px) of the damped shake. */
const SHAKE_AMP = 5;
/** The longest a strike's lunge holds waiting for its outcome before it lets go on its own. */
const LUNGE_HOLD_MAX_MS = 6000;

/** Whether the page asks for reduced motion (the attack looks then play one short, still flash). */
function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
/** Number of full oscillations the damped shake makes over {@link SHAKE_PULSE_MS}. */
const SHAKE_CYCLES = 3;
/** The dark-red tint a token's container pulses toward at the impact peak (mixed with its rest
 *  tint by a 0→1→0 triangle envelope, returning to the exact rest tint at the end). `0xaa1616` is
 *  `rgba(170, 22, 22, ...)` channel-for-channel (alpha isn't tintable, so it's dropped). */
const PULSE_TINT = 0xaa1616;

/** The live token-node lookup the animator tweens against — `pixiCombatBoard.ts` writes it via
 *  `registerNode`/`unregisterNode`, the animator reads it. */
export type NodeRegistry = {
  get(id: string): Container | undefined;
};

/** A live handle onto the mounted board's `app.ticker` — `get()` returns `null` until
 *  `CombatStage.tsx`'s mount effect populates it (see the module doc), which every tween/shake checks
 *  on each call to decide whether to ride the real ticker or fall back to a bare `requestAnimationFrame`
 *  loop for the narrow pre-mount window. */
export type TickerRef = { get(): Ticker | null };

/** The bridge from the animator to the map's transient float/projectile/impact overlays — a
 *  renderer-agnostic shape (only what's behind them, in `pixiCombatBoard.ts`, is Pixi-specific). */
export type FloatSink = {
  spawn(item: { x: number; y: number; spec: FloatSpec }): void;
  clear(): void;
};

/** An attack's travel (`attack-animations` 3.1). `clear` also drops the board's live attack looks
 *  (a snap jumps past them, drones and reticles that hold over a target included). */
export type ProjectileSink = {
  spawn(item: AttackTravelItem): void;
  clear(): void;
};

export type ImpactSink = {
  /** A plain burst (`tech` recolors it hacker-green for a quick-tech flourish — `pixiCombatBoard.ts`'s
   *  `spawnImpact` reads it to pick the palette), or one target's attack outcome. */
  spawn(item: { x: number; y: number; tech?: boolean } | { attack: AttackOutcomeItem }): void;
  clear(): void;
};

/** The chain link's focus ring (`chained-resolution-sequence` 2.2): the token ids to circle now, `[]` for none. */
export type FocusSink = {
  set(ids: readonly string[]): void;
};

/** Bundled sinks param (keeps the factory within the 3-arg `CODE_STYLE.md` cap). */
export type AnimatorSinks = { float: FloatSink; projectile: ProjectileSink; impact: ImpactSink; focus: FocusSink };

type Easing = (t: number) => number;
const easeOutQuad: Easing = (t) => 1 - (1 - t) * (1 - t);

/** `tween()`'s pacing — bundled into one param (with `props`, `node`) to stay within
 *  `CODE_STYLE.md`'s `max-params` cap of 3. `ticker` is required — every caller inside
 *  `makePixiAnimator` closes over the animator's own {@link TickerRef}. */
type TweenOpts = { durationMs: number; easing?: Easing; ticker: TickerRef };

/**
 * Tween `node`'s numeric properties in `props` to their target values over `opts.durationMs`. Drives
 * itself off the live `app.ticker` when `opts.ticker.get()` is populated (so every visual
 * mutation on this surface genuinely rides `ticker.maxFPS`), falling back to a bare
 * `requestAnimationFrame` loop only in the pre-mount window before a ticker exists (see the module
 * doc). Returns the settling `Promise` plus a `cancel()` that stops the loop wherever it is (a newer
 * move, a snap, or unmount supersedes it) without resolving `done`.
 */
function tween(node: Container, props: Partial<Record<"x" | "y" | "alpha", number>>, opts: TweenOpts): { done: Promise<void>; cancel: () => void } {
  // A `setTokens` rebuild (`pixiCombatBoard.ts`) can destroy the very container a tween is running
  // against, out from under it. The failure this guards: a mirrored forced_move token reaches the
  // right server cell but its dblclick-to-inspect times out forever. A destroyed Pixi `Container`
  // nulls its internal `_position`
  // (`Container.destroy()`), so reading/writing `.x`/`.y` on it throws a TypeError — an uncaught throw
  // from inside the driving callback (rAF OR a ticker tick, either loop) aborts that frame BEFORE it
  // reaches either the reschedule or `resolve()`, so the chain silently stops forever: `runStepAnimation`'s
  // `onDone` never fires, `PresentationDirector.playMove` never `commitFromTarget`s, and presentation
  // (what `hitTestToken` reads) stays permanently lagging at the pre-move cell. Guarded at BOTH the
  // entry (a fresh per-segment `tween()` call from `runStepAnimation`'s chain, reading `node[k]` into
  // `start` up front) and every per-frame tick (destroyed mid-flight, not just at call time) — treat
  // either as the documented "node absent" case: resolve immediately, nothing left to animate, the
  // state commit that already happened server-side stays unblocked. This guard must survive BOTH the
  // ticker path and the rAF fallback — it does, since `step()` below is the shared body either loop
  // calls.
  if (node.destroyed) return { done: Promise.resolve(), cancel: () => {} };
  const { durationMs, easing = easeOutQuad, ticker } = opts;
  const keys = Object.keys(props) as ("x" | "y" | "alpha")[];
  const start: Record<string, number> = {};
  for (const k of keys) start[k] = node[k];
  const liveTicker = ticker.get();
  let raf = 0;
  let tickerFn: (() => void) | null = null;
  let cancelled = false;
  const detach = (): void => {
    if (tickerFn && liveTicker) liveTicker.remove(tickerFn);
    if (raf) cancelAnimationFrame(raf);
  };
  const done = new Promise<void>((resolve) => {
    const t0 = performance.now();
    const step = () => {
      if (cancelled) return;
      // Per-tick destroyed guard — fires on EVERY invocation of `step`, whether it was scheduled by
      // `requestAnimationFrame` or the live `app.ticker`.
      if (node.destroyed) {
        detach();
        resolve();
        return;
      }
      const t = Math.min(1, (performance.now() - t0) / durationMs);
      const e = easing(t);
      for (const k of keys) node[k] = start[k]! + (props[k]! - start[k]!) * e;
      if (t < 1) {
        if (!liveTicker) raf = requestAnimationFrame(step);
        // else: still `add`ed to the live ticker, which calls `step` again next tick unprompted.
      } else {
        detach();
        resolve();
      }
    };
    if (liveTicker) {
      tickerFn = step;
      liveTicker.add(tickerFn);
    } else {
      raf = requestAnimationFrame(step);
    }
  });
  return {
    done,
    cancel: () => {
      cancelled = true;
      detach();
    },
  };
}

/**
 * Animate `node` through `points` (world px, origin first) one tile at a time — a chain of
 * per-segment **ease-out** tweens, so the token decelerates into each cell for a rhythmic
 * step-step-step rather than one continuous slide. Places the node at the origin first, then
 * settles exactly on the last point. Returns a cancel handle — calling it stops the chain wherever
 * it is. `onDone` fires once the node reaches the final tile (NOT on cancel).
 */
export function runStepAnimation(node: Container, points: { x: number; y: number }[], opts: { onDone: () => void; ticker: TickerRef }): { cancel: () => void } {
  const { onDone, ticker } = opts;
  let i = 0;
  let current: { cancel: () => void } | null = null;
  let cancelled = false;
  const step = () => {
    if (cancelled) return;
    if (i >= points.length - 1) {
      onDone();
      return;
    }
    const target = points[i + 1]!;
    const t = tween(node, { x: target.x, y: target.y }, { durationMs: MOVE_STEP_MS, ticker });
    current = t;
    void t.done.then(() => {
      if (cancelled) return;
      i += 1;
      step();
    });
  };
  node.x = points[0]!.x;
  node.y = points[0]!.y;
  step();
  return {
    cancel: () => {
      cancelled = true;
      current?.cancel();
    },
  };
}

/** Tween a node's opacity to `target` over `FLOURISH_MS`, resolving (and restoring `opts.restore`) on
 *  finish. `restore`/`ticker` bundled into one options object to stay within `CODE_STYLE.md`'s
 *  `max-params` cap of 3. */
function fade(node: Container, target: number, opts: { restore?: number; ticker: TickerRef }): Promise<void> {
  return tween(node, { alpha: target }, { durationMs: FLOURISH_MS, ticker: opts.ticker }).done.then(() => {
    if (opts.restore !== undefined) node.alpha = opts.restore;
  });
}

/** Run `fn` on the next tick (so a just-committed token has time to be registered) — the live
 *  `app.ticker`'s one-shot `addOnce` when populated, `requestAnimationFrame` only in the
 *  pre-mount window. */
function nextFrame(fn: () => void, ticker: TickerRef): void {
  const liveTicker = ticker.get();
  if (liveTicker) liveTicker.addOnce(() => fn());
  else requestAnimationFrame(() => fn());
}

/** Linear-mix `base`/`peak` (`0xRRGGBB`) by factor `k` (0 = base, 1 = peak). At k=0 it returns
 *  `base` exactly, so a 0→…→0 envelope restores the rest tint precisely. */
function mixColor(base: number, peak: number, k: number): number {
  const br = (base >> 16) & 0xff;
  const bg = (base >> 8) & 0xff;
  const bb = base & 0xff;
  const pr = (peak >> 16) & 0xff;
  const pg = (peak >> 8) & 0xff;
  const pb = peak & 0xff;
  const r = Math.round(br + (pr - br) * k);
  const g = Math.round(bg + (pg - bg) * k);
  const b = Math.round(bb + (pb - bb) * k);
  return (r << 16) | (g << 8) | b;
}

/**
 * Resolve an {@link AnimAnchor} to a world-px point in `tokensContainer`'s (untransformed) space —
 * the same space every overlay container (move-range, attack wash, the VFX pool) already renders
 * in, since they're all direct, untransformed children of the shared `world` root. A `combatant`
 * anchor needs its registered container's position + size (`.width`/`.height` give the footprint
 * bounds directly); a `cell` anchor is a closed-form cell-center, no node lookup at all. Returns
 * `null` for an absent `combatant` node.
 */
function anchorPoint(anchor: AnimAnchor, registry: NodeRegistry, cellPx: number): { x: number; y: number } | null {
  if (anchor.kind === "cell") {
    const cell = asSquareCell(anchor.cell); // this surface is 2d-square-only (`combatCells.ts`)
    return { x: cell.x * cellPx + cellPx / 2, y: cell.y * cellPx + cellPx / 2 };
  }
  const node = registry.get(anchor.id);
  if (!node) return null;
  return { x: node.x + node.width / 2, y: node.y + node.height / 2 };
}

/**
 * Build the Pixi-backed {@link PresentationAnimator} over a node registry. The in-flight
 * animation bookkeeping (`track`/`active`) is renderer-agnostic Promise plumbing, not
 * Pixi-coupled. `config` bundles `cellPx` + the
 * {@link TickerRef} into one object to stay within `CODE_STYLE.md`'s `max-params` cap of 3.
 */
export function makePixiAnimator(registry: NodeRegistry, sinks: AnimatorSinks, config: { cellPx: number; ticker: TickerRef }): {
  move(id: string, points: { x: number; y: number }[]): Promise<void>;
  blink(id: string): Promise<void>;
  fadeIn(id: string): Promise<void>;
  fadeOut(id: string): Promise<void>;
  float(id: string, spec: FloatSpec): Promise<void>;
  projectile(fromId: string, to: AnimAnchor, travel?: TravelSpec): Promise<void>;
  impact(at: AnimAnchor, spec?: ImpactSpec): Promise<void>;
  techBurst(at: AnimAnchor): Promise<void>;
  shakeAndPulse(id: string, punch?: number): Promise<void>;
  sound(cue: SfxCue): void;
  focus(ids: readonly string[]): void;
  cancelAll(): void;
} {
  const { cellPx, ticker } = config;
  const { float: floatSink, projectile: projectileSink, impact: impactSink, focus: focusSink } = sinks;
  // The pump is serial (at most one beat plays at a time), but a single beat can run TWO
  // concurrent animations (the `attack` beat's float + shakeAndPulse), so this is a Set, not a
  // single slot.
  const active = new Set<{ cancel: () => void; resolve: () => void }>();

  function track(start: (settle: () => void) => () => void): Promise<void> {
    return new Promise<void>((resolve) => {
      const handle = { cancel: () => {}, resolve: () => {} };
      const settle = () => {
        active.delete(handle);
        resolve();
      };
      handle.resolve = settle;
      handle.cancel = start(settle);
      active.add(handle);
    });
  }

  /** A beat that paces for `ms` and draws nothing itself (its sink item does). */
  function paced(ms: number): Promise<void> {
    return track((settle) => {
      const timer = setTimeout(settle, ms);
      return () => clearTimeout(timer);
    });
  }

  /** A registered token as an fx unit: its id, centre and half-width, or `undefined` when unmounted. */
  function unitOf(id: string): FxUnit | undefined {
    const node = registry.get(id);
    if (!node) return undefined;
    return { id, x: node.x + node.width / 2, y: node.y + node.height / 2, r: Math.max(node.width, node.height) / 2 };
  }

  // ── Token perturbations for the attack looks (`attack-animations` 3.1) ──────────────────────
  // A strike's lunge, a heavy round's recoil and a miss's dodge move token containers, which only
  // this module may touch outside a move. Each is an OFFSET from the container's rest position,
  // captured when the first offset starts; when the offset returns to zero the container is put
  // back at its EXACT rest, and `cancelAll` does the same mid-way (the discipline `shakeAndPulse`
  // follows, so the next `setTokens` never starts from a stray position).
  const offsets = new Map<string, { node: Container; restX: number; restY: number; ox: number; oy: number; stop: () => void }>();
  const timers = new Set<ReturnType<typeof setTimeout>>();

  function later(ms: number, fn: () => void): void {
    const timer = setTimeout(() => {
      timers.delete(timer);
      fn();
    }, ms);
    timers.add(timer);
  }

  function offsetTo(id: string, to: { x: number; y: number; ms: number }): void {
    const node = registry.get(id);
    if (!node || node.destroyed) return;
    let o = offsets.get(id);
    if (o !== undefined && o.node !== node) {
      o.stop();
      offsets.delete(id);
      o = undefined;
    }
    // Returning to rest with no offset running is a no-op: never capture a "rest" mid-walk.
    if (o === undefined && to.x === 0 && to.y === 0) return;
    if (o === undefined) {
      o = { node, restX: node.x, restY: node.y, ox: 0, oy: 0, stop: () => {} };
      offsets.set(id, o);
    }
    o.stop();
    const entry = o;
    const from = { x: entry.ox, y: entry.oy };
    const start = performance.now();
    const liveTicker = ticker.get();
    let raf = 0;
    let stopped = false;
    const stop = () => {
      stopped = true;
      if (liveTicker) liveTicker.remove(step);
      if (raf) cancelAnimationFrame(raf);
    };
    function step(): void {
      if (stopped) return;
      if (node!.destroyed) {
        stop();
        offsets.delete(id);
        return;
      }
      const t = Math.min(1, (performance.now() - start) / to.ms);
      const e = easeOutQuad(t);
      entry.ox = from.x + (to.x - from.x) * e;
      entry.oy = from.y + (to.y - from.y) * e;
      node!.x = entry.restX + entry.ox;
      node!.y = entry.restY + entry.oy;
      if (t >= 1) {
        stop();
        if (to.x === 0 && to.y === 0) {
          node!.x = entry.restX; // EXACT rest
          node!.y = entry.restY;
          offsets.delete(id);
        }
        return;
      }
      if (!liveTicker) raf = requestAnimationFrame(step);
    }
    entry.stop = stop;
    if (liveTicker) liveTicker.add(step);
    else raf = requestAnimationFrame(step);
  }

  function restoreOffsets(): void {
    for (const timer of timers) clearTimeout(timer);
    timers.clear();
    for (const o of offsets.values()) {
      o.stop();
      if (!o.node.destroyed) {
        o.node.x = o.restX;
        o.node.y = o.restY;
      }
    }
    offsets.clear();
  }

  /** The attacker's half of a travel: a strike lunges a third of a cell and holds; a heavy round recoils. */
  function perturbForTravel(fx: AttackFx, attacker: FxUnit, to: { x: number; y: number }): void {
    const dx = to.x - attacker.x;
    const dy = to.y - attacker.y;
    const d = Math.hypot(dx, dy) || 1;
    if (fx.family === "strike" && fx.thrown !== true) {
      offsetTo(attacker.id, { x: (dx / d) * cellPx * 0.3, y: (dy / d) * cellPx * 0.3, ms: 160 });
      // Never hold a lunge forever: an outcome that never comes (a skipped beat) still lets go.
      later(LUNGE_HOLD_MAX_MS, () => offsetTo(attacker.id, { x: 0, y: 0, ms: 140 }));
    } else if (fx.family === "heavy") {
      offsetTo(attacker.id, { x: (-dx / d) * 5, y: (-dy / d) * 5, ms: 50 });
      later(60, () => offsetTo(attacker.id, { x: 0, y: 0, ms: 180 }));
    }
  }

  /** The strike snaps back once its slash has landed (or whiffed). */
  function releaseAfterOutcome(spec: ImpactSpec): void {
    if (spec.fx.family === "strike" && spec.fx.thrown !== true && offsets.has(spec.from)) later(170, () => offsetTo(spec.from, { x: 0, y: 0, ms: 140 }));
  }

  /** A miss the target visibly sidesteps: a swing, a beam, or anything caught in a template. */
  function dodges(spec: ImpactSpec): boolean {
    const f = spec.fx.family;
    return (f === "strike" && spec.fx.thrown !== true) || f === "beam" || (spec.area === true && f !== "missile" && f !== "swarm");
  }

  function dodge(target: FxUnit, attacker: FxUnit): void {
    if (target.id === "") return;
    const dx = target.x - attacker.x;
    const dy = target.y - attacker.y;
    const d = Math.hypot(dx, dy) || 1;
    offsetTo(target.id, { x: (dx / d) * 5, y: (dy / d) * 5, ms: 80 });
    later(180, () => offsetTo(target.id, { x: 0, y: 0, ms: 140 }));
  }

  return {
    move(id, points) {
      const node = registry.get(id);
      if (!node) return Promise.resolve();
      return track((settle) => runStepAnimation(node, points, { onDone: settle, ticker }).cancel);
    },
    blink(id) {
      const node = registry.get(id);
      if (!node) return Promise.resolve();
      return fade(node, 0.25, { restore: 1, ticker });
    },
    fadeIn(id) {
      return new Promise<void>((resolve) => {
        nextFrame(() => {
          const node = registry.get(id);
          if (!node) {
            resolve();
            return;
          }
          node.alpha = 0;
          void fade(node, 1, { ticker }).then(resolve);
        }, ticker);
      });
    },
    fadeOut(id) {
      const node = registry.get(id);
      if (!node) return Promise.resolve();
      return fade(node, 0, { ticker });
    },
    float(id, spec) {
      const node = registry.get(id);
      // No mounted node → nothing to float above; resolve at once (the state still committed in
      // the beat, so the invariant holds — only the flourish is skipped).
      if (!node) return Promise.resolve();
      // Centre-x, TOP-y of the token's footprint, read straight off the container (§5's invariant).
      floatSink.spawn({ x: node.x + node.width / 2, y: node.y, spec });
      return track((settle) => {
        const pace = spec.tone === "caption" ? CAPTION_PACING_MS : spec.followOn === true ? FOLLOW_ON_FLOAT_PACING_MS : FLOAT_PACING_MS;
        const timer = setTimeout(settle, pace);
        return () => clearTimeout(timer);
      });
    },
    projectile(fromId, to, travel) {
      const attacker = unitOf(fromId);
      // No mounted attacker node → nothing to fly FROM; resolve at once (the outcome beat right
      // behind it still plays and commits state — only the flourish is skipped).
      if (!attacker) return Promise.resolve();
      const toPt = anchorPoint(to, registry, cellPx);
      // A `combatant`-anchored `to` with no mounted node → nothing to fly TO; resolve at once.
      if (!toPt) return Promise.resolve();
      const fx = travel?.fx ?? DEFAULT_ATTACK_FX;
      const targets = (travel?.targets ?? (to.kind === "combatant" ? [to.id] : [])).flatMap((id) => unitOf(id) ?? []);
      const reduced = prefersReducedMotion();
      projectileSink.spawn({ fx, attacker, to: toPt, targets, reduced });
      if (!reduced) perturbForTravel(fx, attacker, toPt);
      return paced(reduced ? REDUCED_TRAVEL_MS : travelMs(fx, targets.length));
    },
    impact(at, spec) {
      const pt = anchorPoint(at, registry, cellPx);
      if (spec === undefined) {
        if (!pt) return Promise.resolve();
        impactSink.spawn(pt);
        return paced(IMPACT_PACING_MS);
      }
      const reduced = prefersReducedMotion();
      // The strike's lunge snaps back whatever happens to the outcome's own anchor.
      if (!reduced && spec.outcome !== "land") releaseAfterOutcome(spec);
      // No mounted node for a `combatant` anchor → nowhere to land; resolve at once (the outcome
      // beat right behind it still plays and commits state — only the flourish is skipped).
      const attacker = unitOf(spec.from);
      if (!pt || !attacker) return Promise.resolve();
      const target: FxUnit = at.kind === "combatant" ? (unitOf(at.id) ?? { id: at.id, ...pt, r: cellPx / 2 }) : { id: "", ...pt, r: cellPx / 2 };
      impactSink.spawn({ attack: { fx: spec.fx, outcome: spec.outcome, attacker, at: target, area: spec.area === true, reduced } });
      if (!reduced && spec.outcome === "miss" && dodges(spec)) dodge(target, attacker);
      return paced(spec.outcome === "land" ? DETONATION_MS : spec.area === true ? AREA_OUTCOME_MS : OUTCOME_MS);
    },
    techBurst(at) {
      // Same pooled burst as `impact` (SAME sink, SAME pacing) — only `tech: true` differs, which
      // `pixiCombatBoard.ts`'s `spawnImpact` reads to pick the hacker-green palette instead of the
      // ballistic fireball's orange.
      const pt = anchorPoint(at, registry, cellPx);
      if (!pt) return Promise.resolve();
      impactSink.spawn({ ...pt, tech: true });
      return track((settle) => {
        const timer = setTimeout(settle, IMPACT_PACING_MS);
        return () => clearTimeout(timer);
      });
    },
    shakeAndPulse(id, punch = 1) {
      const node = registry.get(id);
      // No mounted node → nothing to punch; resolve at once (the `attack` beat already committed
      // the target and floats its outcome — only this flourish is skipped).
      if (!node) return Promise.resolve();
      // Capture the node's EXACT rest position + tint up front: whether the shake ends naturally
      // or is cancelled, we restore precisely here so the next `setTokens` rebuild is never fighting
      // a stray perturbation — the load-bearing safety property this animator maintains throughout.
      const restX = node.x;
      const restY = node.y;
      const restTint = node.tint;
      const liveTicker = ticker.get();
      return track((settle) => {
        let raf = 0;
        let tickerFn: (() => void) | null = null;
        let done = false;
        const start = performance.now();
        const detach = (): void => {
          if (tickerFn && liveTicker) liveTicker.remove(tickerFn);
          if (raf) cancelAnimationFrame(raf);
        };
        const finish = () => {
          if (done) return;
          done = true;
          detach();
          // Same `setTokens`-mid-flight-destroy hazard `tween()` guards against (see its comment):
          // a destroyed container's `.x`/`.y`/`.tint` writes would throw and strand this beat's
          // Promise forever. Nothing to restore on a destroyed node — it's gone.
          if (!node.destroyed) {
            node.x = restX; // EXACT rest — never a fraction off, even on cancel
            node.y = restY;
            node.tint = restTint; // restore the rest tint exactly
          }
          settle();
        };
        // `step` is the shared body EITHER loop calls (a bare rAF callback pre-mount, or a live
        // `app.ticker` tick once mounted) — the per-tick destroyed guard below fires either way.
        const step = () => {
          if (done) return;
          if (node.destroyed) {
            finish();
            return;
          }
          const t = Math.min(1, (performance.now() - start) / SHAKE_PULSE_MS);
          const damp = 1 - t;
          node.x = restX + Math.sin(t * Math.PI * 2 * SHAKE_CYCLES) * SHAKE_AMP * punch * damp;
          node.y = restY;
          const env = 1 - Math.abs(2 * t - 1); // 0 → 1 (peak impact) → 0
          node.tint = mixColor(restTint, PULSE_TINT, env);
          if (t >= 1) {
            finish();
            return;
          }
          if (!liveTicker) raf = requestAnimationFrame(step);
          // else: still `add`ed to the live ticker, which calls `step` again next tick unprompted.
        };
        if (liveTicker) {
          tickerFn = step;
          liveTicker.add(tickerFn);
        } else {
          raf = requestAnimationFrame(step);
        }
        return finish; // cancelAll → finish: stop the loop AND restore the exact rest position/tint
      });
    },
    sound(cue) {
      // Renderer-agnostic — a plain `sfxEngine` call, untouched by which renderer is mounted.
      if (cue === "impact") sfxEngine.impact();
      else sfxEngine.miss();
    },
    focus(ids) {
      focusSink.set(ids);
    },
    cancelAll() {
      focusSink.set([]); // a snap jumps past the chain, so nothing is in focus
      restoreOffsets(); // a lunge or dodge mid-way returns to its exact rest
      for (const handle of [...active]) {
        handle.cancel();
        handle.resolve();
      }
      floatSink.clear(); // drop every live float on a snap (catch-up jumps past the flourish)
      projectileSink.clear(); // drop every live projectile trail on a snap, same reason
      impactSink.clear(); // drop every live impact burst on a snap, same reason
    },
  };
}
